/**
 * Mémoire Auria V1.
 *
 * Aucune donnée du système précédent n'est lue.
 * Les utilisateurs commencent avec une mémoire Auria neuve.
 *
 * Firestore :
 *   users/{uid}/auriaSessions/{sessionId}
 *   users/{uid}/auriaPreferences/main
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase-config.js";

const SCHEMA_VERSION = 1;
const MAX_LOCAL_HISTORY = 24;

const HISTORY_KEY = (uid) => `auria:v1:history:${uid || "anon"}`;
const PREFS_KEY = (uid) => `auria:v1:preferences:${uid || "anon"}`;
const CHOICES_KEY = (uid) => `auria:v1:choices:${uid || "anon"}`;

function emptyLearning() {
  return {
    tracks: {},
    naturals: {},
    breathings: {},
    durations: {},
    volumes: {},
  };
}

function emptyPreferences() {
  return {
    schemaVersion: SCHEMA_VERSION,
    favoriteNatureSoundIds: [],
    onboarding: {
      natureCompletedAt: null,
    },
    learning: emptyLearning(),
    favorites: [],
    hiddenSessions: [],
  };
}

function normalizePreferences(value) {
  const source = value && typeof value === "object" ? value : {};
  const learning = source.learning && typeof source.learning === "object"
    ? source.learning
    : {};

  return {
    ...emptyPreferences(),
    ...source,
    schemaVersion: SCHEMA_VERSION,
    favoriteNatureSoundIds: Array.isArray(source.favoriteNatureSoundIds)
      ? source.favoriteNatureSoundIds.slice(0, 3)
      : [],
    onboarding: {
      natureCompletedAt: source.onboarding?.natureCompletedAt || null,
    },
    learning: {
      tracks: learning.tracks || {},
      naturals: learning.naturals || {},
      breathings: learning.breathings || {},
      durations: learning.durations || {},
      volumes: learning.volumes || {},
    },
    favorites: Array.isArray(source.favorites) ? source.favorites.slice(0, 40) : [],
    hiddenSessions: Array.isArray(source.hiddenSessions) ? source.hiddenSessions.slice(0, 40) : [],
  };
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}


export function clearLegacyAIClientCache() {
  try {
    const keys = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (
        key &&
        (key.startsWith("ai:") ||
          key.startsWith("harmonauria:ai:") ||
          key === "harmonauria:ai:titleMap" ||
          key === "harmonauria:ai:customTitleMap")
      ) {
        keys.push(key);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {}
}

export function readLocalHistory(uid) {
  const value = readJson(HISTORY_KEY(uid), []);
  return Array.isArray(value) ? value : [];
}

export function appendLocalHistoryEntry(uid, entry) {
  const next = [entry, ...readLocalHistory(uid)].slice(0, MAX_LOCAL_HISTORY);
  writeJson(HISTORY_KEY(uid), next);
}

export function getRecentTrackIds(uid) {
  const result = [];
  for (const entry of readLocalHistory(uid)) {
    for (const id of entry.trackIds || []) {
      if (id && !result.includes(id)) result.push(id);
      if (result.length >= 15) return result;
    }
  }
  return result;
}

export function getRecentAmbianceIds(uid) {
  const result = [];
  for (const entry of readLocalHistory(uid)) {
    for (const id of entry.ambianceIds || []) {
      if (id && !result.includes(id)) result.push(id);
      if (result.length >= 8) return result;
    }
  }
  return result;
}

function sessionSignature(session) {
  return (session?.arc?.tracks || [])
    .map((track) => track?.id)
    .filter(Boolean)
    .join("|");
}

function sessionSnapshot(session) {
  return {
    title: session?.title || null,
    narrative: session?.narrative || null,
    why: Array.isArray(session?.why) ? session.why : [],
    arc: session?.arc || null,
    ambiances: session?.ambiances || [],
    breathing: session?.breathing || null,
    metadata: session?.metadata || null,
    context: session?.context || null,
  };
}

function nowMillis() {
  return Date.now();
}

function updateStat(container, key, value, weight = 1) {
  const safeWeight = Math.max(0.1, Number(weight) || 1);
  const previous = container[key] || { mean: 0, samples: 0, lastUsedAt: null };
  const previousSamples = Math.max(0, Number(previous.samples) || 0);
  const nextSamples = previousSamples + safeWeight;
  /* Plancher d'adaptation : les ressentis récents gardent du poids, sinon
     la moyenne se fige et Auria cesse d'évoluer avec l'utilisateur. */
  const alpha = Math.min(1, Math.max(safeWeight / nextSamples, safeWeight * 0.12));
  const nextMean = Number(previous.mean || 0) * (1 - alpha) + Number(value || 0) * alpha;

  container[key] = {
    mean: Math.round(nextMean * 1000) / 1000,
    samples: Math.round(nextSamples * 100) / 100,
    lastUsedAt: nowMillis(),
  };
}

function updateLearning(preferences, intent, sessionData, feedback) {
  if (!intent) return preferences;

  const next = normalizePreferences(preferences);
  const learning = typeof structuredClone === "function"
    ? structuredClone(next.learning)
    : JSON.parse(JSON.stringify(next.learning));

  const hasBefore =
    sessionData?.stateBefore !== null &&
    sessionData?.stateBefore !== undefined &&
    Number.isFinite(Number(sessionData.stateBefore));
  const hasAfter =
    feedback?.stateAfter !== null &&
    feedback?.stateAfter !== undefined &&
    Number.isFinite(Number(feedback.stateAfter));
  const stateBefore = hasBefore ? Number(sessionData.stateBefore) : null;
  const stateAfter = hasAfter ? Number(feedback.stateAfter) : null;
  const hasStatePair = hasBefore && hasAfter;
  const wordWeight = Number(feedback?.feedbackWeight) || 0;
  const rawSignal = hasStatePair ? stateAfter - stateBefore + wordWeight : wordWeight;
  const effectSignal = Math.max(-4, Math.min(4, rawSignal));
  /* Seul un retour explicite alimente l'apprentissage : curseur déplacé,
     « j'ai adoré » ou « ne plus proposer ». La durée n'est plus un signal. */
  const feedbackGiven = feedback?.feedbackGiven === true;

  if (feedbackGiven && (hasStatePair || wordWeight !== 0)) {
    const tracks = { ...(learning.tracks[intent] || {}) };
    for (const id of sessionData?.trackIds || []) {
      updateStat(tracks, id, effectSignal, 1);
    }
    learning.tracks = { ...learning.tracks, [intent]: tracks };

    const naturals = { ...(learning.naturals[intent] || {}) };
    const initialIds = new Set(sessionData?.ambianceIds || []);
    const finalAmbiances = Array.isArray(feedback?.finalAmbiances)
      ? feedback.finalAmbiances
      : [];
    const finalIds = new Set(finalAmbiances.map((item) => item?.id).filter(Boolean));

    for (const id of finalIds) {
      const wasAddedManually = !initialIds.has(id);
      updateStat(naturals, id, effectSignal, wasAddedManually ? 0.65 : 1);
    }

    for (const id of initialIds) {
      if (!finalIds.has(id)) updateStat(naturals, id, -1, 0.5);
    }
    learning.naturals = { ...learning.naturals, [intent]: naturals };

    if (sessionData?.withBreathing && sessionData?.breathingId) {
      const breathings = { ...(learning.breathings[intent] || {}) };
      updateStat(breathings, String(sessionData.breathingId), effectSignal, 0.8);
      learning.breathings = { ...learning.breathings, [intent]: breathings };
    }
  }

  /* Le volume n'est retenu que dans une séance où l'utilisateur s'est
     exprimé : sinon Auria réapprendrait le volume qu'elle vient de composer. */
  const finalAmbiances = Array.isArray(feedback?.finalAmbiances)
    ? feedback.finalAmbiances
    : [];
  if (feedbackGiven && finalAmbiances.length) {
    const volumes = { ...(learning.volumes[intent] || {}) };
    for (const ambiance of finalAmbiances) {
      if (!ambiance?.id || !Number.isFinite(Number(ambiance.volume))) continue;
      updateStat(volumes, ambiance.id, Number(ambiance.volume), 1);
    }
    learning.volumes = { ...learning.volumes, [intent]: volumes };
  }

  return { ...next, learning };
}

function cachePreferences(uid, preferences) {
  const normalized = normalizePreferences(preferences);
  writeJson(PREFS_KEY(uid), normalized);
  writeJson(CHOICES_KEY(uid), { favorites: normalized.favorites, hiddenSessions: normalized.hiddenSessions });
  return normalized;
}

async function mutatePreferences(uid, mutate) {
  if (!uid) return emptyPreferences();
  const ref = doc(db, "users", uid, "auriaPreferences", "main");
  const result = await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    const current = normalizePreferences(snapshot.exists() ? snapshot.data() : null);
    const next = normalizePreferences(await mutate(current));
    transaction.set(ref, { ...next, updatedAt: serverTimestamp() }, { merge: true });
    return next;
  });
  return cachePreferences(uid, result);
}

export async function loadPreferences(uid) {
  if (!uid) return emptyPreferences();
  try {
    const snap = await getDoc(doc(db, "users", uid, "auriaPreferences", "main"));
    const normalized = normalizePreferences(snap.exists() ? snap.data() : null);
    writeJson(PREFS_KEY(uid), normalized);
    writeJson(CHOICES_KEY(uid), {
      favorites: normalized.favorites,
      hiddenSessions: normalized.hiddenSessions,
    });
    return normalized;
  } catch (error) {
    console.warn("Auria preferences unavailable, local fallback used.", error);
    return normalizePreferences(readJson(PREFS_KEY(uid), null));
  }
}

export function loadPreferencesSync(uid) {
  return normalizePreferences(readJson(PREFS_KEY(uid), null));
}

export function subscribePreferences(uid, onValue, onError) {
  if (!uid) return () => {};
  return onSnapshot(doc(db, "users", uid, "auriaPreferences", "main"), (snapshot) => {
    onValue?.(cachePreferences(uid, snapshot.exists() ? snapshot.data() : null));
  }, onError);
}

export async function saveFavoriteNatureSoundIds(uid, ids) {
  if (!uid) return null;
  const cleanIds = [...new Set((ids || []).filter(Boolean))].slice(0, 3);
  if (cleanIds.length !== 3) throw new Error("Trois ambiances naturelles doivent être sélectionnées.");
  return mutatePreferences(uid, (current) => ({ ...current, favoriteNatureSoundIds: cleanIds, onboarding: { ...current.onboarding, natureCompletedAt: current.onboarding?.natureCompletedAt || new Date().toISOString() } }));
}

export async function saveSessionStart(uid, session, stateBefore) {
  if (!uid || !session?.id) throw new Error("Session Auria invalide.");

  const payload = {
    schemaVersion: SCHEMA_VERSION,
    sessionId: session.id,
    intent: session.context?.intent || null,
    durationKey: session.context?.durationKey || null,
    stateBefore:
      stateBefore !== null &&
      stateBefore !== undefined &&
      Number.isFinite(Number(stateBefore))
        ? Number(stateBefore)
        : null,
    stateAfter: null,
    title: session.title || null,
    trackIds: (session.arc?.tracks || []).map((track) => track.id).filter(Boolean),
    ambianceIds: (session.ambiances || []).map((item) => item.id).filter(Boolean),
    initialAmbiances: (session.ambiances || []).map((item) => ({
      id: item.id,
      volume: Number(item.volume) || 0,
    })),
    breathingId: session.context?.withBreathing ? session.breathing?.id || null : null,
    breathingName: session.context?.withBreathing ? session.breathing?.name || null : null,
    withBreathing: session.context?.withBreathing === true,
    regenerationCount: session.context?.regenerationCount || 0,
    feedbackGiven: false,
    listenedSeconds: 0,
    startedAt: serverTimestamp(),
    completedAt: null,
  };

  const sessionRef = doc(db, "users", uid, "auriaSessions", session.id);
  const created = await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(sessionRef);
    if (existing.exists()) return false;
    transaction.set(sessionRef, payload);
    return true;
  });
  if (created) appendLocalHistoryEntry(uid, { sessionId: session.id, intent: payload.intent, trackIds: payload.trackIds, ambianceIds: payload.ambianceIds, at: nowMillis() });
  return session.id;
}

export async function saveSessionFeedback(uid, sessionId, { stateAfter, listenedSeconds = 0, feedbackWeight = 0, feedbackSkipped = false, finalAmbiances = [] }) {
  if (!uid || !sessionId) throw new Error("Session Auria invalide.");
  const sessionRef = doc(db, "users", uid, "auriaSessions", sessionId);
  const preferencesRef = doc(db, "users", uid, "auriaPreferences", "main");
  const nextPreferences = await runTransaction(db, async (transaction) => {
    const sessionSnap = await transaction.get(sessionRef);
    const preferencesSnap = await transaction.get(preferencesRef);
    if (!sessionSnap.exists()) throw new Error("Le début de la session n’a pas été enregistré.");
    const sessionData = sessionSnap.data();
    if (sessionData.feedbackApplied === true) return normalizePreferences(preferencesSnap.exists() ? preferencesSnap.data() : null);
    const numericAfter = Number(stateAfter);
    const hasAfter = stateAfter !== null && stateAfter !== undefined && Number.isFinite(numericAfter);
    const numericBefore = Number(sessionData.stateBefore);
    const effect = hasAfter && Number.isFinite(numericBefore) ? numericAfter - numericBefore : null;
    const weight = Number(feedbackWeight) || 0;
    const feedbackGiven = !feedbackSkipped && (hasAfter || weight !== 0);
    const finalPayload = (finalAmbiances || []).map((item) => ({ id: item?.id, volume: Math.max(0, Math.min(1, Number(item?.volume) || 0)) })).filter((item) => item.id);
    const current = normalizePreferences(preferencesSnap.exists() ? preferencesSnap.data() : null);
    const learned = updateLearning(current, sessionData.intent, sessionData, { stateAfter: hasAfter ? numericAfter : null, feedbackGiven, feedbackWeight: weight, finalAmbiances: finalPayload });
    transaction.set(sessionRef, { stateAfter: hasAfter ? numericAfter : null, effect, feedbackWeight: weight, feedbackSkipped: Boolean(feedbackSkipped), feedbackGiven, feedbackApplied: true, listenedSeconds: Math.max(0, Number(listenedSeconds) || 0), finalAmbiances: finalPayload, completedAt: serverTimestamp() }, { merge: true });
    transaction.set(preferencesRef, { ...learned, updatedAt: serverTimestamp() }, { merge: true });
    return learned;
  });
  cachePreferences(uid, nextPreferences);
  return true;
}

export async function loadUserChoices(uid) {
  if (!uid) return { favorites: [], hiddenSessions: [] };
  const prefs = await loadPreferences(uid);
  return { favorites: prefs.favorites, hiddenSessions: prefs.hiddenSessions };
}

export async function hideSession(uid, session) {
  const signature = sessionSignature(session);
  if (!signature) return loadPreferences(uid);
  return mutatePreferences(uid, (current) => {
    const hiddenEntry = { hiddenId: `hidden-${Date.now()}`, signature, hiddenAt: Date.now(), ...sessionSnapshot(session) };
    return { ...current, hiddenSessions: [hiddenEntry, ...current.hiddenSessions.filter((item) => item.signature !== signature)], favorites: current.favorites.filter((item) => item.signature !== signature) };
  });
}

export async function unhideSession(uid, hiddenId) {
  return mutatePreferences(uid, (current) => ({ ...current, hiddenSessions: current.hiddenSessions.filter((item) => item.hiddenId !== hiddenId) }));
}

export async function unhideAllSessions(uid) {
  return mutatePreferences(uid, (current) => ({ ...current, hiddenSessions: [] }));
}

export async function addFavoriteSession(uid, session) {
  const signature = sessionSignature(session);
  if (!signature) return loadPreferences(uid);
  return mutatePreferences(uid, (current) => {
    const favorite = { favoriteId: `favorite-${Date.now()}`, signature, savedAt: Date.now(), ...sessionSnapshot(session) };
    return { ...current, favorites: [favorite, ...current.favorites.filter((item) => item.signature !== signature)], hiddenSessions: current.hiddenSessions.filter((item) => item.signature !== signature) };
  });
}

export async function removeFavorite(uid, favoriteId) {
  return mutatePreferences(uid, (current) => ({ ...current, favorites: current.favorites.filter((item) => item.favoriteId !== favoriteId) }));
}

export async function renameFavoriteBySignature(uid, session, title) {
  const signature = sessionSignature(session);
  const clean = String(title || "").trim().slice(0, 40);
  return mutatePreferences(uid, (current) => ({ ...current, favorites: current.favorites.map((item) => item.signature === signature ? { ...item, title: clean || item.title } : item) }));
}

export async function getAIDashboardData(uid) {
  if (!uid) return null;
  try {
    const sessionsRef = collection(db, "users", uid, "auriaSessions");
    const sessionsQuery = query(sessionsRef, orderBy("startedAt", "desc"), limit(100));
    const snap = await getDocs(sessionsQuery);
    const sessions = snap.docs.map((item) => item.data());
    if (!sessions.length) return null;

    /* `completed` : ancien champ des sessions écrites avant la refonte. */
    const hasFeedback = (item) => item.feedbackGiven === true || item.completed === true;
    const rated = sessions.filter(
      (item) => Number.isFinite(Number(item.effect)) && hasFeedback(item),
    );
    const learningSessions = sessions.filter(hasFeedback).length;
    const averageEffect = rated.length
      ? rated.reduce((sum, item) => sum + Number(item.effect), 0) / rated.length
      : 0;

    const naturalEffects = {};
    for (const session of rated) {
      const ids = (session.finalAmbiances || session.initialAmbiances || [])
        .map((item) => (typeof item === "string" ? item : item?.id))
        .filter(Boolean);
      for (const id of ids) {
        const current = naturalEffects[id] || { sum: 0, samples: 0 };
        current.sum += Number(session.effect) || 0;
        current.samples += 1;
        naturalEffects[id] = current;
      }
    }

    const bestNatural = Object.entries(naturalEffects)
      .filter(([, stat]) => stat.samples >= 3)
      .map(([id, stat]) => ({ id, mean: stat.sum / stat.samples, samples: stat.samples }))
      .sort((a, b) => b.mean - a.mean)[0] || null;

    const timestampMillis = (value) => {
      if (!value) return null;
      try {
        if (typeof value.toDate === "function") return value.toDate().getTime();
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : null;
      } catch {
        return null;
      }
    };

    const parisDay = (millis) => {
      try {
        const formatter = new Intl.DateTimeFormat("fr-FR", {
          timeZone: "Europe/Paris",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        const [day, month, year] = formatter.format(new Date(millis)).split("/");
        return `${year}-${month}-${day}`;
      } catch {
        return null;
      }
    };

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const monthSessions = sessions.filter((item) => {
      const millis = timestampMillis(item.startedAt);
      return millis !== null && millis >= monthStart;
    }).length;

    const completedDays = [...new Set(
      sessions
        .filter(hasFeedback)
        .map((item) => timestampMillis(item.startedAt))
        .filter((value) => value !== null)
        .map(parisDay)
        .filter(Boolean),
    )].sort();

    const dayMillis = 24 * 60 * 60 * 1000;
    const isConsecutive = (left, right) => {
      const a = new Date(`${left}T00:00:00Z`).getTime();
      const b = new Date(`${right}T00:00:00Z`).getTime();
      return Math.round((b - a) / dayMillis) === 1;
    };

    let longest = 0;
    let running = 0;
    for (let index = 0; index < completedDays.length; index += 1) {
      running = index > 0 && isConsecutive(completedDays[index - 1], completedDays[index])
        ? running + 1
        : 1;
      longest = Math.max(longest, running);
    }

    const today = parisDay(Date.now());
    const yesterday = parisDay(Date.now() - dayMillis);
    let current = 0;
    if (completedDays.length) {
      const last = completedDays.at(-1);
      if (last === today || last === yesterday) {
        current = 1;
        for (let index = completedDays.length - 2; index >= 0; index -= 1) {
          if (!isConsecutive(completedDays[index], completedDays[index + 1])) break;
          current += 1;
        }
      }
    }

    return {
      totalSessions: sessions.length,
      completedSessions: sessions.filter(hasFeedback).length,
      sessionsWithFeedback: rated.length,
      learningSessions,
      averageEffect: Math.round(averageEffect * 10) / 10,
      monthSessions,
      streak: { current, longest },
      bestNatural,
      learningStage:
        rated.length >= 10 ? "personalized" : rated.length >= 4 ? "learning" : "discovering",
    };
  } catch (error) {
    console.warn("Auria dashboard unavailable.", error);
    return null;
  }
}
